import { useEffect, useRef } from 'react';

type DrawCallback = (ctx: CanvasRenderingContext2D, time: number, deltaTime: number, width: number, height: number) => void;

export const useCanvasAnimation = (
    draw: DrawCallback,
    dependencies: any[] = []
) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const requestRef = useRef<number | undefined>(undefined);
    const startTimeRef = useRef<number>(performance.now());
    const lastTimeRef = useRef<number>(performance.now());
    const dimensionsRef = useRef({ width: 0, height: 0 });
    
    // Keep a mutable ref to the callback to avoid restarting the animation loop on every render
    const drawRef = useRef(draw);
    useEffect(() => {
        drawRef.current = draw;
    }); // Update on every render

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let animationActive = true;

        // Function to update canvas size with proper DPI scaling
        const updateCanvasSize = () => {
            if (!canvas) return;
            
            const dpr = window.devicePixelRatio || 1;
            const rect = canvas.getBoundingClientRect();
            
            // Skip if rect is zero (element not visible)
            if (rect.width === 0 || rect.height === 0) return;
            
            // Always update when forced (dimensionsRef reset to 0,0) or when dimensions actually changed
            const needsUpdate = dimensionsRef.current.width !== rect.width || 
                               dimensionsRef.current.height !== rect.height ||
                               (dimensionsRef.current.width === 0 && dimensionsRef.current.height === 0);
            
            if (needsUpdate) {
                dimensionsRef.current = { width: rect.width, height: rect.height };
                
                // Set the internal resolution to match display density
                canvas.width = Math.floor(rect.width * dpr);
                canvas.height = Math.floor(rect.height * dpr);
                
                // Reset and scale context to match DPI
                ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset transform
                ctx.scale(dpr, dpr);
            }
        };

        // Initial size setup
        updateCanvasSize();
        
        // Force a second update after a brief delay to ensure proper sizing after mount
        const initialSizeTimeout = setTimeout(() => {
            dimensionsRef.current = { width: 0, height: 0 }; // Force recalculation
            updateCanvasSize();
        }, 50);

        // Watch for size changes using ResizeObserver (better than window resize for mobile)
        const resizeObserver = new ResizeObserver(() => {
            // Force a recalculation by resetting dimensions
            dimensionsRef.current = { width: 0, height: 0 };
            updateCanvasSize();
        });
        resizeObserver.observe(canvas);

        const animate = (time: number) => {
            if (!animationActive) return;

            // time is performance.now() passed by RAF
            const totalElapsed = (time - startTimeRef.current) / 1000;
            const deltaTime = (time - lastTimeRef.current) / 1000;
            lastTimeRef.current = time;

            // Use the logical dimensions for drawing
            const { width, height } = dimensionsRef.current;
            if (width > 0 && height > 0) {
                drawRef.current(ctx, totalElapsed, deltaTime, width, height);
            }
            
            requestRef.current = requestAnimationFrame(animate);
        };

        requestRef.current = requestAnimationFrame(animate);

        return () => {
            animationActive = false;
            clearTimeout(initialSizeTimeout);
            resizeObserver.disconnect();
            if (requestRef.current) {
                cancelAnimationFrame(requestRef.current);
            }
            // Reset dimensions ref so next mount recalculates
            dimensionsRef.current = { width: 0, height: 0 };
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ ...dependencies]); 
    // We intentionally spread dependencies to trigger re-setup of canvas if needed
    // though mostly we rely on drawRef for logic updates.

    return canvasRef;
};